import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@core/feature';
import { BonusWidgetComponent, SdModalService } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { OccLoyaltyProgramService } from '@user/data-access';
import { CurrentProfileService, LoyaltyProgramService, LoyaltyQrService } from '@user/feature';
import { QRCodeComponent } from 'angularx-qrcode';
import { BehaviorSubject, catchError, combineLatest, interval, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { LoyaltyQrModalComponent } from '../loyalty-qr-modal';
import { UpdateQrButtonComponent } from '../update-qr-button';

@Component({
  selector: 'sd-loyalty-qr-card',
  imports: [QRCodeComponent, BonusWidgetComponent, UpdateQrButtonComponent, AsyncPipe],
  templateUrl: './loyalty-qr-card.component.html',
  styleUrls: ['./loyalty-qr-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoyaltyQrCardComponent implements OnInit {
  private currentProfileService = inject(CurrentProfileService);
  private deviceService = inject(DeviceService);
  private modalService = inject(SdModalService);
  private loyaltyProgramFeatureService = inject(LoyaltyProgramService);
  private loyaltyQrService = inject(LoyaltyQrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly qrCode$ = new BehaviorSubject<string | null>(null);
  private readonly expiredAt$ = new BehaviorSubject(0);

  private readonly featureFlagService = inject(FeatureFlagService);
  private occLoyaltyProgramService = inject(OccLoyaltyProgramService);
  protected readonly useNewQrEnableFlag$ = this.featureFlagService.isEnabled('useNewQrEnableFlag');

  isTablet = toSignal(this.deviceService.isTabletMdUp$, { initialValue: false });

  profileData = toSignal(
    combineLatest({
      profile: this.currentProfileService.get(),
      bonusBalance: this.loyaltyProgramFeatureService.getBonusBalance(),
      useNewQrEnableFlag: this.useNewQrEnableFlag$,
    }).pipe(
      switchMap(({ profile, bonusBalance, useNewQrEnableFlag }) => {
        const uuid = profile?.user?.rosaUuid;
        if (!profile?.isLoggedIn || profile?.isLegal || !uuid) {
          return of(null);
        }

        if (!bonusBalance) {
          return of(null);
        }

        if (!bonusBalance.segmentName) {
          return of(null);
        }

        const segmentName = bonusBalance.segmentName;

        if (useNewQrEnableFlag) {
          return this.qrCode$.pipe(
            filter((qrCode): qrCode is string => !!qrCode),
            map((qrCode) => ({
              qrCode,
              bonusBalance,
              segmentName,
            })),
          );
        } else {
          return this.occLoyaltyProgramService.getOldLoyaltyQr().pipe(
            map((res) => res?.data ?? null),
            catchError(() => of(null)),
            map((qrCode) =>
              qrCode
                ? {
                    qrCode,
                    bonusBalance,
                    segmentName,
                  }
                : null,
            ),
          );
        }
      }),
    ),
    { initialValue: null },
  );

  ngOnInit(): void {
    this.useNewQrEnableFlag$
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.loyaltyQrService.loadLoyaltyQr();

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loyaltyQrService
      .getLoyaltyQr()
      .pipe(
        filter((qr) => !!qr?.data && qr.ttlSeconds > 0),
        distinctUntilChanged((prev, curr) => prev.data === curr.data && prev.ttlSeconds === curr.ttlSeconds),
        tap(({ ttlSeconds, data }) => {
          this.expiredAt$.next(Date.now() + ttlSeconds * 1000);
          this.qrCode$.next(data);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // каждую секунду проверяем жив ли куар чтобы при истечении времени жизни сразу обновить его
    interval(1000)
      .pipe(
        tap(() => {
          const expiredAt = this.expiredAt$.value;
          if (expiredAt > 0 && Date.now() >= expiredAt) {
            this.loyaltyQrService.loadLoyaltyQr();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openQrModal(qrCode: string): void {
    const modal = this.modalService.open(LoyaltyQrModalComponent, {
      modalType: 'fullscreen',
      hideCloseButton: false,
    });
    modal.contentRef.setInput('qrCode', qrCode);

    this.qrCode$
      .pipe(
        filter((qr): qr is string => !!qr),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        modal.contentRef.setInput('qrCode', data);
      });
  }
}
